import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Badge } from '../ui';
import { registrarResultadoJuego } from '../../lib/supabase';

const RESULTADO_LABELS = {
  W: 'Victoria',
  L: 'Derrota',
  D: 'Empate'
};

const JuegoScoreModal = ({ isOpen, juego, onClose, onSaved }) => {
  const [puntosFavor, setPuntosFavor] = useState('');
  const [puntosContra, setPuntosContra] = useState('');
  const [comentarios, setComentarios] = useState('');
  const [notasTecnicas, setNotasTecnicas] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canEdit = useMemo(() => (juego?.estatus || '').toLowerCase() === 'terminado', [juego?.estatus]);

  useEffect(() => {
    if (!isOpen) return;
    setPuntosFavor(juego?.puntos_favor ?? '');
    setPuntosContra(juego?.puntos_contra ?? '');
    setComentarios(juego?.comentarios || '');
    setNotasTecnicas(juego?.notas_tecnicas || '');
    setError('');
  }, [isOpen, juego]);

  const handleSave = async () => {
    if (!juego?.id || saving) return;
    const favor = puntosFavor === '' ? null : Number.parseInt(puntosFavor, 10);
    const contra = puntosContra === '' ? null : Number.parseInt(puntosContra, 10);

    if (favor === null || Number.isNaN(favor)) {
      setError('Ingresa los puntos a favor.');
      return;
    }
    if (contra === null || Number.isNaN(contra)) {
      setError('Ingresa los puntos en contra.');
      return;
    }

    try {
      setSaving(true);
      const updated = await registrarResultadoJuego(juego.id, {
        puntos_favor: favor,
        puntos_contra: contra,
        comentarios: comentarios?.trim() || null,
        notas_tecnicas: notasTecnicas?.trim() || null
      });
      onSaved?.(updated);
      onClose?.();
    } catch (err) {
      console.error('Error guardando resultado:', err);
      setError('No se pudo guardar el resultado.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="bg-white rounded-2xl p-6 w-full max-w-2xl shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-black font-display font-bold text-lg">Marcador del juego</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-lg transition-all"
          >
            ×
          </button>
        </div>
      <div className="space-y-4">
        {!canEdit && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Termina primero la asistencia para activar el marcador.
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-medium text-slate-600">Puntos a favor</label>
            <Input
              type="number"
              min={0}
              disabled={!canEdit}
              value={puntosFavor}
              onChange={(e) => setPuntosFavor(e.target.value)}
              placeholder="Ej. 24"
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">Puntos en contra</label>
            <Input
              type="number"
              min={0}
              disabled={!canEdit}
              value={puntosContra}
              onChange={(e) => setPuntosContra(e.target.value)}
              placeholder="Ej. 14"
              className="mt-1"
            />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-medium text-slate-600">Comentarios</label>
            <textarea
              rows={4}
              disabled={!canEdit}
              value={comentarios}
              onChange={(e) => setComentarios(e.target.value)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60"
              placeholder="Resumen del partido..."
            />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-600">Notas internas</label>
            <textarea
              rows={4}
              disabled={!canEdit}
              value={notasTecnicas}
              onChange={(e) => setNotasTecnicas(e.target.value)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60"
              placeholder="Notas para el staff..."
            />
          </div>
        </div>

        {juego?.resultado && (
          <Badge variant="secondary" className="uppercase tracking-wide">
            {RESULTADO_LABELS[juego.resultado] || juego.resultado}
          </Badge>
        )}

        {error && <div className="text-sm text-red-600">{error}</div>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cerrar
          </Button>
          <Button onClick={handleSave} disabled={!canEdit || saving}>
            {saving ? 'Guardando...' : 'Guardar resultado'}
          </Button>
        </div>
      </div>
      </div>
    </div>
  );
};

export default JuegoScoreModal;
